import { SkyButton, SkyTooltip, useSkyTheme } from "@skypointcloud/skypoint-ui";
import {
  Desktop16Regular,
  WeatherMoon16Regular,
  WeatherSunny16Regular,
} from "@fluentui/react-icons";
import type { ThemePreference } from "../../lib/theme";

const OPTIONS: {
  value: ThemePreference;
  label: string;
  tooltip: string;
  icon: typeof WeatherSunny16Regular;
}[] = [
  { value: "light", label: "Light", tooltip: "Light appearance", icon: WeatherSunny16Regular },
  { value: "dark", label: "Dark", tooltip: "Dark appearance", icon: WeatherMoon16Regular },
  {
    value: "system",
    label: "System",
    tooltip: "Match your device's light or dark setting",
    icon: Desktop16Regular,
  },
];

/**
 * Light / Dark / System, in the profile menu (UI-GUIDELINES: a role="group"
 * of small SkyButtons with aria-pressed; the selected chip is styled globally).
 *
 * Changing mode is SkyProvider's setMode, as in skySchedule; ThemeSync
 * remembers it. Nothing here touches the network — the preference is per device.
 */
export function ThemeSwitcher({ className = "" }: { className?: string }) {
  const { mode, setMode } = useSkyTheme();

  return (
    <div
      className={`flex items-center gap-1 rounded-lg bg-muted p-1 ${className}`}
      role="group"
      aria-label="Appearance"
    >
      {OPTIONS.map(({ value, label, tooltip, icon: Icon }) => (
        <SkyTooltip
          key={value}
          content={tooltip}
        >
          <SkyButton
            size="small"
            appearance={mode === value ? "secondary" : "transparent"}
            icon={Icon}
            aria-pressed={mode === value}
            data-testid={`theme-option-${value}`}
            onClick={() => setMode(value)}
            className="flex-1"
          >
            {label}
          </SkyButton>
        </SkyTooltip>
      ))}
    </div>
  );
}
